import { getTextAnnotationBox, useImageAnnotatorContext } from "@msuite/image-annotator";
import type { TextAnnotation as TextAnnotationType } from "@msuite/utils/types";
import { useTranslation } from "../../localization";
import { AnnotationDragHandle } from "./annotation-drag-handle";

/** Props Interface */
interface TextAnnotationProps {
	annotation: TextAnnotationType;
}

export function TextAnnotation({ annotation }: TextAnnotationProps) {
	/** Context */
	const context = useImageAnnotatorContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const isSelected = context.controller.selectionId === annotation.id;
	const isEditing = context.controller.textEditId === annotation.id;
	const box = getTextAnnotationBox(
		annotation,
		context.baseScale,
		t("image_annotator.text_placeholder"),
	);
	const color = annotation.strokeColor || "#fff";
	const strokeWidth = annotation.strokeWidth / context.baseScale;

	/** Functions */
	function handleOnClick(e: React.MouseEvent) {
		if (context.controller.isReadOnly) return;
		/** While a creation tool is armed the click belongs to the canvas, not to selection. */
		if (context.controller.tool !== "select") return;
		e.stopPropagation();
		context.controller.setSelectionId(annotation.id);
	}

	/**
	 * Editing is a double click rather than a second single click, because ending a drag also
	 * produces a click on the annotation — which would otherwise open the editor every time the
	 * label was moved.
	 */
	function handleOnDoubleClick(e: React.MouseEvent) {
		if (context.controller.isReadOnly) return;
		if (context.controller.tool !== "select") return;
		e.stopPropagation();
		context.controller.handleOnStartTextEdit(annotation.id);
	}

	/** Render */
	return (
		<g
			style={{ cursor: context.controller.isReadOnly ? undefined : "pointer" }}
			onClick={handleOnClick}
			onDoubleClick={handleOnDoubleClick}
		>
			{annotation.callout && (
				<>
					<line
						x1={annotation.x + box.width / 2}
						y1={annotation.y + box.height}
						x2={annotation.callout.x}
						y2={annotation.callout.y}
						stroke={isSelected ? "var(--color-yellow-500)" : color}
						strokeWidth={strokeWidth}
					/>
					<circle
						cx={annotation.callout.x}
						cy={annotation.callout.y}
						r={5 / context.baseScale}
						stroke={isSelected ? "var(--color-yellow-500)" : color}
						strokeWidth={strokeWidth}
					/>
					{isSelected && !context.controller.isReadOnly && (
						<AnnotationDragHandle
							cx={annotation.callout.x}
							cy={annotation.callout.y}
							kind="txt_callout"
							annotationId={annotation.id}
						/>
					)}
				</>
			)}
			<rect
				x={box.x}
				y={box.y}
				width={box.width}
				height={box.height}
				fill="rgba(0,0,0,.82)"
				stroke={isSelected ? "var(--color-yellow-500)" : color}
				strokeWidth={strokeWidth}
			/>
			{/** While the in-place editor is open it draws the text, so the SVG copy steps aside. */}
			{!isEditing &&
				box.lines.map((line, index) => (
					<text
						key={index}
						x={annotation.x + box.paddingX}
						y={annotation.y + box.paddingY + (index + 0.85) * box.lineHeight}
						fill={color}
						fontSize={box.fontSize}
						fontFamily="Arial"
						pointerEvents="none"
					>
						{line.trim()}
					</text>
				))}
			{isSelected && !context.controller.isReadOnly && !isEditing && (
				<AnnotationDragHandle
					cx={annotation.x}
					cy={annotation.y}
					kind="txt_pos"
					annotationId={annotation.id}
				/>
			)}
		</g>
	);
}
